"use client";

import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import { supabase } from "../../supabaseClient";

export default function DatixReport() {
  const [searchParams] = useSearchParams();
  const linkedPatientId = searchParams.get("patient") || "";
  const linkedPatientName = searchParams.get("name") || "";
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    title: "",
    incident_datetime: "",
    location: "",
    category: "",
    subcategory: "",
    patient_involved: "",
    injury: "",
    severity: "",
    description: "",
    immediate_actions: "",
    people_involved: "",
    witnesses: "",
    contributing_factors: "",
  });

  /* Auto-fill current date & time */
  useEffect(() => {
    const now = new Date();
    const iso = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16); // yyyy-MM-ddTHH:mm

    setForm((f) => ({
      ...f,
      incident_datetime: iso,
      ...(linkedPatientId ? { patient_involved: "Yes" } : {}),
    }));
  }, [linkedPatientId]);

  const handleChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.from("datix_reports").insert([
      {
        title: form.title,
        incident_datetime: new Date(form.incident_datetime),
        location: form.location,
        category: form.category,
        subcategory: form.subcategory || null,
        patient_involved: form.patient_involved,
        injury: form.injury,
        severity: form.severity,
        description: form.description,
        immediate_actions: form.immediate_actions,
        people_involved: form.people_involved,
        witnesses: form.witnesses,
        contributing_factors: form.contributing_factors,
        patient_id: linkedPatientId || null,
      },
    ]);

    if (error) {
      console.error(error);
      alert("Error submitting DATIX report");
    } else {
      alert("DATIX report submitted successfully");
      setForm({
        title: "",
        incident_datetime: "",
        location: "",
        category: "",
        subcategory: "",
        patient_involved: "",
        injury: "",
        severity: "",
        description: "",
        immediate_actions: "",
        people_involved: "",
        witnesses: "",
        contributing_factors: "",
      });
    }

    setLoading(false);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="nhsuk-width-container nhsuk-u-padding-4">
        <h1 className="nhsuk-heading-l">DATIX Incident Report</h1>
        <p className="nhsuk-body">
          Please complete this form to report an incident or near miss. Provide
          as much detail as possible.
        </p>

        {linkedPatientId && (
          <div className="nhsuk-inset-text">
            <strong>Linked patient record</strong>
            <p className="nhsuk-body nhsuk-u-margin-bottom-0">
              This DATIX report will be linked to {linkedPatientName || "the selected patient"} and shown in their staff clinical record.
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Incident Title */}
          <div>
            <label className="nhsuk-label">Incident Title</label>
            <input
              className="nhsuk-input nhsuk-u-width-full"
              placeholder="e.g. Medication error at Pharmacy"
              value={form.title}
              onChange={(e) => handleChange("title", e.target.value)}
              required
            />
          </div>

          {/* Date / Location / Category */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="nhsuk-label">Incident Date & Time</label>
              <input
                type="datetime-local"
                className="nhsuk-input"
                value={form.incident_datetime}
                onChange={(e) =>
                  handleChange("incident_datetime", e.target.value)
                }
                required
              />
            </div>

            <div>
              <label className="nhsuk-label">Location</label>
              <select
                className="nhsuk-select"
                value={form.location}
                onChange={(e) => handleChange("location", e.target.value)}
                required
              >
                <option value="">Select</option>
                {[
                  "A&E",
                  "Wards",
                  "Theatres",
                  "Outpatients",
                  "Pharmacy",
                  "Reception",
                  "Car Park",
                  "Other",
                ].map((l) => (
                  <option key={l}>{l}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="nhsuk-label">Incident Category</label>

              <select
                className="nhsuk-select"
                value={form.category}
                onChange={(e) => handleChange("category", e.target.value)}
                required
              >
                <option value="">Select</option>
                {[
                  "Clinical Incident",
                  "Medication / Prescribing",
                  "Security / Aggression",
                  "Slips / Trips / Falls",
                  "Equipment",
                  "Info Governance",
                  "Safeguarding",
                  "Infection Prevention & Control",
                  "Environmental",
                  "Other",
                ].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>

              <input
                className="nhsuk-input nhsuk-u-margin-top-2"
                value={form.subcategory}
                placeholder="Subcategory (optional)"
                onChange={(e) => handleChange("subcategory", e.target.value)}
              />
            </div>
          </div>

          {/* Patient / Injury / Severity */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="nhsuk-label">Patient Involved?</label>
              <select
                className="nhsuk-select"
                value={form.patient_involved}
                onChange={(e) =>
                  handleChange("patient_involved", e.target.value)
                }
                required
              >
                <option value="">Select</option>
                <option>Yes</option>
                <option>No</option>
                <option>Near Miss</option>
              </select>
            </div>

            <div>
              <label className="nhsuk-label">Injury?</label>
              <select
                className="nhsuk-select"
                value={form.injury}
                onChange={(e) => handleChange("injury", e.target.value)}
                required
              >
                <option value="">Select</option>
                <option>Yes</option>
                <option>No</option>
              </select>
            </div>

            <div>
              <label className="nhsuk-label">Severity</label>
              <select
                className="nhsuk-select"
                value={form.severity}
                onChange={(e) => handleChange("severity", e.target.value)}
                required
              >
                <option value="">Select</option>
                <option>No Harm</option>
                <option>Low</option>
                <option>Moderate</option>
                <option>Severe</option>
              </select>
            </div>
          </div>

          {/* Narrative */}
          <div>
            <label className="nhsuk-label">What happened?</label>
            <textarea
              className="nhsuk-textarea"
              rows={4}
              value={form.description}
              onChange={(e) => handleChange("description", e.target.value)}
              required
            />
          </div>

          <div>
            <label className="nhsuk-label">Immediate actions taken</label>
            <textarea
              className="nhsuk-textarea"
              rows={3}
              value={form.immediate_actions}
              onChange={(e) =>
                handleChange("immediate_actions", e.target.value)
              }
            />
          </div>

          {/* People / Witnesses */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="nhsuk-label">People involved</label>
              <textarea
                className="nhsuk-textarea"
                rows={3}
                value={form.people_involved}
                onChange={(e) =>
                  handleChange("people_involved", e.target.value)
                }
              />
            </div>

            <div>
              <label className="nhsuk-label">Witnesses</label>
              <textarea
                className="nhsuk-textarea"
                rows={3}
                value={form.witnesses}
                onChange={(e) => handleChange("witnesses", e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="nhsuk-label">
              Contributing factors (optional)
            </label>
            <textarea
              className="nhsuk-textarea"
              rows={2}
              value={form.contributing_factors}
              onChange={(e) =>
                handleChange("contributing_factors", e.target.value)
              }
            />
          </div>

          <button
            className="nhsuk-button nhsuk-u-width-full"
            disabled={loading}
          >
            {loading ? "Submitting…" : "Submit DATIX"}
          </button>
        </form>
      </main>

      <Footer />
    </div>
  );
}
